// src/cockpit4/components/QuantTradingPanel.tsx
// ============================================
// 量化工作台（站内） - P2 任务：项目中心增加站内量化界面
//
// 功能：
//   1. 实时行情（A 股指数/个股）——通过 /quant-api/realtime 真实数据（新浪财经，GBK 解码）
//   2. 黄金外汇量化 / 股票期权量化两个产品入口（打开外部工作站 trade.xshetech.com）
//   3. 白皮书铁律一：无实时数据时显示"暂无实时行情"，绝不填充假数据
//
// 数据源：
//   GET /quant-api/realtime?symbol=sh000001,sz399001,...  → { success, quotes: [...] }
//   quote 字段：symbol / name / price / change / changePercent / high / low / amount / date
// ============================================

import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, ExternalLink, RefreshCw, Activity, BarChart3, AlertCircle, Loader2, Coins } from "lucide-react";

/** 实时行情（与 server.mjs parseSinaRealtime 字段对齐） */
interface RealtimeQuote {
  symbol: string;
  name: string;
  open: number;
  prevClose: number;
  price: number;
  high: number;
  low: number;
  volume: number;
  amount: number;
  change: number;
  changePercent: number;
  date: string;
  exchange: string;
  currency: string;
}

/** 关注的符号：A 股指数 + 龙头个股 */
const WATCH_SYMBOLS = [
  "sh000001", // 上证指数
  "sz399001", // 深证成指
  "sh000300", // 沪深300
  "sz399006", // 创业板指
  "sh600519", // 贵州茅台
  "sh600036", // 招商银行
  "sz000001", // 平安银行
  "sz300750", // 宁德时代
];

/** 量化产品（外部工作站入口） */
const QUANT_PRODUCTS = [
  {
    id: "gold_forex",
    name: "黄金外汇量化",
    tagline: "Gold Factory · 黄金/外汇 EA 量化",
    desc: "COMEX 黄金、现货黄金、外汇货币对量化交易信号与策略，接入真实行情。",
    features: ["黄金 EA 策略", "外汇多品种", "信号扫描", "自动风控"],
    launchUrl: "https://trade.xshetech.com/?product=gold_forex",
    color: "#F59E0B",
    icon: Activity,
  },
  {
    id: "stock_options",
    name: "股票期权量化",
    tagline: "Stock & Options · A 股/期权量化",
    desc: "A 股选股、期权定价与组合策略，基于新浪/东方财富真实行情。",
    features: ["A 股选股", "期权定价", "动量扫描", "估值筛选"],
    launchUrl: "https://trade.xshetech.com/?product=stock_options",
    color: "#38BDF8",
    icon: BarChart3,
  },
];

/** 格式化涨跌幅 */
function fmtPct(v: number): string {
  return `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;
}

/** 格式化金额（万/亿） */
function fmtAmount(v: number): string {
  if (!v) return "—";
  if (v >= 1e8) return `${(v / 1e8).toFixed(2)}亿`;
  if (v >= 1e4) return `${(v / 1e4).toFixed(1)}万`;
  return v.toFixed(0);
}

export default function QuantTradingPanel() {
  const [quotes, setQuotes] = useState<RealtimeQuote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<string>("");

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`/quant-api/realtime?symbol=${WATCH_SYMBOLS.join(",")}`);
      if (!resp.ok) {
        setQuotes([]);
        setError(`行情服务不可用（HTTP ${resp.status}）`);
        return;
      }
      const json = await resp.json();
      if (json?.success && Array.isArray(json.quotes) && json.quotes.length > 0) {
        setQuotes(json.quotes);
        setFetchedAt(new Date().toLocaleTimeString("zh-CN"));
      } else {
        // 真实数据为空：显示暂无（白皮书铁律一：不填充假数据）
        setQuotes([]);
        setError(json?.error || "暂无实时行情数据");
      }
    } catch (err: any) {
      setQuotes([]);
      setError(`获取行情失败: ${err?.message || err}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuotes();
    // 每 60 秒自动刷新一次
    const timer = setInterval(fetchQuotes, 60_000);
    return () => clearInterval(timer);
  }, [fetchQuotes]);

  return (
    <div className="h-full w-full overflow-y-auto">
      <div className="space-y-4">
        {/* 顶部：标题 + 刷新 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <TrendingUp size={14} className="text-amber-400" />
            </div>
            <div>
              <h3 className="text-[14px] font-semibold text-white">量化工作台</h3>
              <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                站内行情 · 外部工作站入口
              </p>
            </div>
          </div>
          <button
            onClick={fetchQuotes}
            disabled={loading}
            className="flex items-center gap-1 px-2.5 py-1 rounded border border-white/[0.06] hover:bg-white/[0.04] text-[10px] font-mono text-gray-400 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
            刷新行情
          </button>
        </div>

        {/* 实时行情 */}
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06] bg-white/[0.01]">
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
              A 股实时行情（新浪财经）
            </span>
            {fetchedAt && (
              <span className="text-[9px] font-mono text-gray-600">更新于 {fetchedAt}</span>
            )}
          </div>

          {loading && quotes.length === 0 ? (
            <div className="flex items-center justify-center gap-2 py-10 text-gray-500">
              <Loader2 size={14} className="animate-spin" />
              <span className="text-[11px]">加载真实行情中...</span>
            </div>
          ) : quotes.length > 0 ? (
            <table className="w-full text-left">
              <thead>
                <tr className="text-[9px] font-mono text-gray-500 uppercase tracking-wider border-b border-white/[0.04]">
                  <th className="px-3 py-2 font-normal">名称</th>
                  <th className="px-3 py-2 font-normal">代码</th>
                  <th className="px-3 py-2 font-normal text-right">最新价</th>
                  <th className="px-3 py-2 font-normal text-right">涨跌</th>
                  <th className="px-3 py-2 font-normal text-right">涨跌幅</th>
                  <th className="px-3 py-2 font-normal text-right">最高</th>
                  <th className="px-3 py-2 font-normal text-right">最低</th>
                  <th className="px-3 py-2 font-normal text-right">成交额</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((q) => {
                  const up = q.changePercent >= 0;
                  const color = up ? "#34D399" : "#F87171";
                  return (
                    <tr key={q.symbol} className="border-b border-white/[0.03] last:border-0 hover:bg-white/[0.02]">
                      <td className="px-3 py-1.5 text-[11px] text-gray-200">{q.name}</td>
                      <td className="px-3 py-1.5 text-[10px] font-mono text-gray-500">{q.symbol}</td>
                      <td className="px-3 py-1.5 text-[11px] font-mono text-right text-white">
                        {q.price ? q.price.toFixed(2) : "—"}
                      </td>
                      <td className="px-3 py-1.5 text-[11px] font-mono text-right" style={{ color }}>
                        {q.change ? (q.change >= 0 ? "+" : "") + q.change.toFixed(2) : "—"}
                      </td>
                      <td className="px-3 py-1.5 text-[11px] font-mono text-right" style={{ color }}>
                        {fmtPct(q.changePercent)}
                      </td>
                      <td className="px-3 py-1.5 text-[11px] font-mono text-right text-gray-400">
                        {q.high ? q.high.toFixed(2) : "—"}
                      </td>
                      <td className="px-3 py-1.5 text-[11px] font-mono text-right text-gray-400">
                        {q.low ? q.low.toFixed(2) : "—"}
                      </td>
                      <td className="px-3 py-1.5 text-[11px] font-mono text-right text-gray-400">
                        {fmtAmount(q.amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="flex items-start gap-2 px-3 py-8 text-gray-500">
              <AlertCircle size={14} className="text-amber-400/70 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[11px] text-gray-400">
                  {error || "暂无实时行情数据"}
                </p>
                <p className="text-[10px] text-gray-600 mt-1">
                  行情服务不可用时展示真实空状态（白皮书铁律一：禁止 mock 假数据）。
                  可点击下方「打开外部量化工作站」进入完整交易界面。
                </p>
              </div>
            </div>
          )}
        </div>

        {/* 量化产品入口 */}
        <div className="grid grid-cols-2 gap-3">
          {QUANT_PRODUCTS.map((p) => {
            const PIcon = p.icon;
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 flex flex-col"
                style={{ borderColor: `${p.color}30` }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className="w-7 h-7 rounded-md flex items-center justify-center"
                    style={{ background: `${p.color}18`, border: `1px solid ${p.color}35` }}
                  >
                    <PIcon size={14} style={{ color: p.color }} />
                  </div>
                  <div>
                    <h4 className="text-[12px] font-semibold text-white">{p.name}</h4>
                    <p className="text-[9px] font-mono text-gray-500">{p.tagline}</p>
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 mb-2">{p.desc}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {p.features.map((f) => (
                    <span
                      key={f}
                      className="px-1.5 py-0.5 rounded bg-white/[0.03] border border-white/[0.06] text-[9px] font-mono text-gray-400"
                    >
                      {f}
                    </span>
                  ))}
                </div>
                <div className="mt-auto">
                  <a
                    href={p.launchUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1.5 w-full py-2 rounded-md text-[11px] font-medium text-white transition-all hover:opacity-90"
                    style={{ background: `linear-gradient(135deg, ${p.color}55, ${p.color}30)`, border: `1px solid ${p.color}45` }}
                  >
                    <ExternalLink size={11} />
                    打开外部量化工作站
                  </a>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* 数据来源说明 */}
        <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/[0.05] text-[9px] text-gray-500 font-mono">
          <Coins size={10} className="text-yellow-500/70 flex-shrink-0" />
          数据来源：新浪财经实时行情（/quant-api/realtime）· 完整交易在外部工作站 trade.xshetech.com
        </div>
      </div>
    </div>
  );
}

